import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CORE_AUTH_CONFIG, CoreAuthConfig } from 'core';
import { ChatbotUserContextAdapter } from '../adapters/chatbot-user-context.adapter';
import { ChatRequestDto } from '../dto/chat-request.dto';
import { ChatResponseDto } from '../dto/chat-response.dto';
import { ConversationRestoreResponseDto } from '../dto/conversation-restore-response.dto';
import { CHATBOT_CHAT_API_URL, CHATBOT_CONVERSATION_API_URL } from '../tokens/chatbot-api.tokens';

@Injectable()
export class ChatbotApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(CORE_AUTH_CONFIG) private readonly coreAuthConfig: CoreAuthConfig,
    @Inject(CHATBOT_CHAT_API_URL) private readonly chatApiUrl: string,
    @Inject(CHATBOT_CONVERSATION_API_URL) private readonly conversationApiUrl: string,
    private readonly userContextAdapter: ChatbotUserContextAdapter,
  ) {}

  sendMessage(request: ChatRequestDto): Observable<ChatResponseDto> {
    return this.requestOptions$().pipe(
      switchMap((options) => this.http.post<ChatResponseDto>(this.chatApiUrl, request, options)),
    );
  }

  getConversation(conversationId: string): Observable<ConversationRestoreResponseDto> {
    const url = this.conversationApiUrl.includes('{conversation_id}')
      ? this.conversationApiUrl.replace('{conversation_id}', encodeURIComponent(conversationId))
      : `${this.conversationApiUrl.replace(/\/$/, '')}/${encodeURIComponent(conversationId)}`;

    return this.requestOptions$().pipe(
      switchMap((options) => this.http.get<ConversationRestoreResponseDto>(url, options)),
    );
  }

  private requestOptions$(): Observable<{ headers: HttpHeaders }> {
    return this.userContextAdapter.getRoleCode$().pipe(
      map((roleCode) => {
        let headers = new HttpHeaders()
          .set('Content-Type', 'application/json')
          .set('Cache-Control', 'no-cache')
          .set('Pragma', 'no-cache')
          .set('RoleCode', roleCode ?? '');

        if (this.coreAuthConfig.api.subscriptionKey) {
          headers = headers.set('Ocp-Apim-Subscription-Key', this.coreAuthConfig.api.subscriptionKey);
        }

        return { headers };
      }),
    );
  }
}
